import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { useSelector } from "react-redux";
import { useModal } from "../../../../../../../hooks/useModal";

import { IoMdAdd, IoMdArrowBack } from "react-icons/io";

import { ModalBackground, NewChatModalContainer } from "../../../common/styled/project";

import CloseButton from "./common/closeButton";
import CurrentRooms from "./chatModal/currentRooms";
import CompanySelection from "./chatModal/companySelection";

const PROFILE_CLASS_NAMES = ["one", "two", "three", "four"];
const FALLBACK_ORG_IMAGE = "/img/icon_function_6.png";

const ChatModal = ({
  project,
  projectRoomList,
  changeModalState,
  participantList,
  getProjectRoom,
}) => {
  const user = useSelector((state) => state?.user?.data[0]);

  const { modal, openModal, closeModal } = useModal();

  const [other, setOther] = useState([]);
  const [createGroup, setCreateGroup] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [checked, setChecked] = useState([]);

  // 회사 목록 분리
  const separateCompanies = (roomList, projectOrgNo) => {
    const allCompanies = Array.from(
      new Map(
        roomList.flatMap((room) => room.orgInfos || []).map((org) => [org.orgNo, org]),
      ).values(),
    );

    const projectCompany = allCompanies.find((org) => org.orgNo === projectOrgNo);
    const otherCompanies = allCompanies.filter((org) => org.orgNo !== projectOrgNo);

    return { projectCompany, otherCompanies };
  };

  // 프로필 이미지 렌더링
  const renderProfileImages = (orgInfos) => {
    const limitedOrgInfos = orgInfos.slice(0, 4);
    const className = PROFILE_CLASS_NAMES[Math.min(limitedOrgInfos.length, 4) - 1];

    const imageElements = limitedOrgInfos.map((info, index) => (
      <img
        key={index}
        src={info?.orgImg ? imgHandler(info.orgImg) : "/img/function_22_bg.png"}
        alt={`cmpny${index + 1}`}
      />
    ));

    return <div className={className}>{imageElements}</div>;
  };

  // 중복 룸 체크
  const isDuplicateRoom = (selectedOrgNos, roomList) => {
    return roomList.some((room) => {
      const roomOrgNos = (room.orgInfos || []).map((o) => o.orgNo).sort((a, b) => a - b);
      return (
        roomOrgNos.length === selectedOrgNos.length &&
        roomOrgNos.every((orgNo, idx) => orgNo === selectedOrgNos[idx])
      );
    });
  };

  useEffect(() => {
    const { projectCompany, otherCompanies } = separateCompanies(projectRoomList, project.orgNo);
    setCreateGroup(projectCompany);
    setOther(otherCompanies);
  }, [projectRoomList, project.orgNo]);

  // 전체 선택/해제
  const handleCheckAll = (e) => {
    setChecked(e.target.checked ? other : []);
  };

  // 개별 체크박스 처리
  const handleIndividualCheck = (item, isChecked) => {
    if (isChecked) {
      setChecked((prev) => [...prev, item]);
    } else {
      setChecked((prev) => prev.filter((c) => c.orgNo !== item.orgNo));
    }
  };

  // 선택 메시지 생성
  const getSelectionMessage = (isCompany) => {
    return isCompany ? "회사를 선택해주세요." : "그룹을 선택해주세요.";
  };

  // 확인 메시지 생성
  const getConfirmationMessage = (checkedCount, isCompany) => {
    const entityType = isCompany ? "회사와" : " 그룹과";
    return [`선택하신 ${checkedCount}개의 ${entityType}`, "채팅룸을 생성하시겠습니까?"];
  };

  const handleAdd = () => {
    if (!createGroup || !checked?.length) {
      const isCompany = project.gbn === "C";
      toast.error(getSelectionMessage(isCompany));
      return;
    }

    const selectedOrgNos = [createGroup, ...checked].map((c) => c.orgNo).sort((a, b) => a - b);

    if (isDuplicateRoom(selectedOrgNos, projectRoomList)) {
      toast.error("이미 존재하는 채팅룸입니다.");
      return;
    }

    const isCompany = project.gbn === "C";
    openModal({
      isOpen: true,
      type: "confirmation",
      title: "채팅룸 생성",
      message: getConfirmationMessage(checked.length, isCompany),
      handleConfirm: () => createRooms(checked),
      handleCancel: () => closeModal(),
    });
  };

  // 채팅룸 생성
  const createRooms = async (checkedItems) => {
    const requestBody = {
      projectNo: project.projectNo,
      gbn: project.gbn,
      orgNoList: [createGroup.orgNo, ...checkedItems.map((item) => item.orgNo)],
      createMberId: user.mberNo,
      updateMberId: user.userName,
    };

    try {
      const response = await axios.put("/api/room", requestBody);

      if (response.status === 200) {
        toast.success("채팅룸이 생성되었습니다.");
        await getProjectRoom(project, participantList);
        changeModalState("");
      }
    } catch (error) {
      console.error("채팅룸 생성 실패:", error);
      toast.error("채팅룸 생성 중 오류가 발생했습니다.");
    } finally {
      closeModal();
    }
  };

  const Sections = [
    <CurrentRooms
      key="current-rooms"
      projectRoomList={projectRoomList}
      renderProfileImages={renderProfileImages}
    />,
    <CompanySelection
      key="company-selection"
      other={other}
      checked={checked}
      handleCheckAll={handleCheckAll}
      handleIndividualCheck={handleIndividualCheck}
      imgHandler={imgHandler}
      fallbackImage={FALLBACK_ORG_IMAGE}
      createGroup={createGroup}
      handleAdd={handleAdd}
      renderProfileImages={renderProfileImages}
    />,
  ];
  return (
    <>
      {modal}
      <ModalBackground onClick={(e) => e.target === e.currentTarget && changeModalState("")}>
        <NewChatModalContainer>
          <div className="p_10">
            <CloseButton changeModalState={changeModalState} />
            <div className="info">
              {tabIndex !== 0 && (
                <button onClick={() => setTabIndex(0)}>
                  <IoMdArrowBack />
                </button>
              )}
              <span>{tabIndex ? "채팅룸 추가" : "채팅방 관리"}</span>
              {tabIndex === 0 && (
                <button onClick={() => setTabIndex(1)}>
                  <IoMdAdd />
                </button>
              )}
            </div>
            <div className="section">{Sections[tabIndex]}</div>
          </div>
        </NewChatModalContainer>
      </ModalBackground>
    </>
  );
};

export default ChatModal;
